import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Table } from '../../components/common';
import toast from '../../components/common/toast';
import NewScheduleModal from '../../components/Schedule/Modals/NewSchedule';
import { UserContext } from '../../contexts/user';
import { GetSchedules } from '../../services/Api/schedules/useApiSchedules';
import { Controls, getRowProps } from '../../components/common/table/tableHelpers';
import { useHistory } from 'react-router-dom';

export default function Schedules() {
    const { hasRolePermission } = useContext(UserContext);
    const [schedules, setSchedules] = useState([]);
    const [filteredSchedules, setFilteredSchedules] = useState(null);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [showInactive, setShowInactive] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [hoveredRow, setHoveredRow] = useState(null);

    const [showCreateScheduleForm, setShowCreateScheduleForm] = useState(false);

    const openCreateScheduleForm = () => {
        setShowCreateScheduleForm(true);
    };

    const closeCreateScheduleForm = () => {
        setShowCreateScheduleForm(false);
    };

    const canCreateSchedules = hasRolePermission('sc');

    const history = useHistory();

    useEffect(() => {
        GetSchedules()
            .then((response) => {
                if (response.data) {
                    setSchedules(response.data);
                    setFilteredSchedules(response.data.filter((s) => s.active));
                    setIsLoading(false);
                }
            })
            .catch((err) => {
                setIsLoading(false);
                console.error(err);
                toast.error();
            });
    }, []);

    const columns = useMemo(
        () => [
            {
                Header: 'ID',
                accessor: 'id',
                hidden: true,
            },
            {
                Header: 'Name',
                accessor: 'name',
            },
            {
                Header: 'Description',
                accessor: 'description',
            },
            {
                Header: 'Company',
                accessor: 'company',
                hidden: true,
            },
            {
                Header: 'Project',
                accessor: 'project',
                hidden: true,
            },
            {
                Header: 'Company (Project)',
                accessor: 'companyproject',
                companyAccessor: 'company',
                projectAccessor: 'project',
                Cell: ({ row, column }) =>
                    `${row.original[column.companyAccessor]} (${
                        row.original[column.projectAccessor]
                    })`,
            },
        ],
        []
    );

    const onRowClick = (row) => history.push(`/schedules/${row.values.id}`);

    const scheduleOptions = schedules.map((scheduleOption) => ({
        label: `${scheduleOption.company}`,
        value: scheduleOption.instanceid,
    }));

    const hideShowInactiveOptions = [
        { value: false, label: 'Hide Inactive' },
        { value: true, label: 'Show All' },
    ];

    const uniqueScheduleOptions = scheduleOptions.filter(
        (value, index, self) =>
            index === self.findIndex((t) => t.value === value.value && t.label === value.label)
    );

    const onCompanyDropdownChange = (e) => {
        const newSelectedCompany = e?.value || null;
        setSelectedCompany(newSelectedCompany);
        filterSchedules(newSelectedCompany, showInactive);
    };

    const onInactiveDropdownChange = (e) => {
        const newShowInactive = e?.value || false;
        setShowInactive(newShowInactive);
        filterSchedules(selectedCompany, newShowInactive);
    };

    const filterSchedules = (instanceId, showAll) => {
        setFilteredSchedules((_) =>
            schedules.filter(
                (s) =>
                    (instanceId ? s.instanceid === instanceId : true) && (showAll ? true : s.active)
            )
        );
    };

    const filters = [
        {
            placeholder: 'Select Company',
            onChange: (e) => onCompanyDropdownChange(e),
            options: uniqueScheduleOptions,
            isClearable: true,
        },
        {
            placeholder: 'Hide Inactive',
            onChange: (e) => onInactiveDropdownChange(e),
            options: hideShowInactiveOptions,
            defaultValue: { value: false, label: 'Hide Inactive' },
            isClearable: false,
        },
    ];

    const controls = (
        <Controls
            filters={filters}
            canAdd={canCreateSchedules}
            addText="Create Schedule"
            onClick={openCreateScheduleForm}
        />
    );

    return (
        <div className="schedules-page">
            <Table
                columns={columns}
                data={filteredSchedules ? filteredSchedules : schedules}
                isLoading={isLoading}
                filterable={true}
                filterPlaceholder={'Search for Schedules...'}
                controls={controls}
                getRowProps={(row) => getRowProps(row, onRowClick, hoveredRow, setHoveredRow)}
            />
            {!showCreateScheduleForm || !canCreateSchedules ? (
                <></>
            ) : (
                <div className="flex justify-center w-full">
                    <NewScheduleModal closeForm={closeCreateScheduleForm} />
                </div>
            )}
        </div>
    );
}
